import { Box, Button, Heading, Label, LabeledControl, Modal, Radio, Switch } from '@zillow/constellation-10';
import { useState } from 'react';
import type { ExportDialogProps, FileType, SelectedGraphs } from './ExportDialog.types';

export const ExportDialog = ({ open, onClose, tabName, graphs, onExport }: ExportDialogProps) => {
  const [fileType, setFileType] = useState<FileType>('pdf');
  const [selectedGraphs, setSelectedGraphs] = useState<SelectedGraphs>(() => {
    const initial: SelectedGraphs = {};
    graphs.forEach((graph) => {
      initial[graph.id] = true;
      graph.subOptions?.forEach((sub) => {
        initial[sub.id] = true;
      });
    });
    return initial;
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleToggleGraph = (graphId: string) => {
    setSelectedGraphs((prev) => {
      const newSelection = { ...prev, [graphId]: !prev[graphId] };

      const graph = graphs.find((g) => g.id === graphId);
      if (graph?.subOptions) {
        graph.subOptions.forEach((sub) => {
          newSelection[sub.id] = newSelection[graphId];
        });
      }

      return newSelection;
    });
  };

  const handleToggleSubOption = (graphId: string, subOptionId: string) => {
    setSelectedGraphs((prev) => {
      const newSelection = { ...prev, [subOptionId]: !prev[subOptionId] };

      const graph = graphs.find((g) => g.id === graphId);
      if (graph?.subOptions) {
        const allSubsSelected = graph.subOptions.every((sub) =>
          sub.id === subOptionId ? newSelection[subOptionId] : newSelection[sub.id],
        );
        newSelection[graphId] = allSubsSelected;
      }

      return newSelection;
    });
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      await onExport(fileType, selectedGraphs);
      onClose();
    } catch (error) {
      // Error is handled by parent component with toast
      // Keep dialog open so user can try again
    } finally {
      setIsExporting(false);
    }
  };

  const hasSelection = Object.values(selectedGraphs).some((selected) => selected);

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      size="xs"
      header={
        <Box css={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Heading level={5}>Export {tabName} data</Heading>
        </Box>
      }
      body={
        <Box css={{ padding: '600', display: 'flex', flexDirection: 'column', gap: '600' }}>
          <Box>
            <Heading level={6} css={{ marginBottom: '400' }}>
              File type
            </Heading>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
              <LabeledControl
                control={
                  <Radio checked={fileType === 'xls'} onChange={() => setFileType('xls')} value="xls" name="fileType" />
                }
                label={<Label>XLS</Label>}
              />
              <LabeledControl
                control={
                  <Radio checked={fileType === 'pdf'} onChange={() => setFileType('pdf')} value="pdf" name="fileType" />
                }
                label={<Label>PDF</Label>}
              />
            </Box>
          </Box>

          <Box>
            <Heading level={6} css={{ marginBottom: '400' }}>
              Data
            </Heading>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
              {graphs.map((graph) => (
                <Box key={graph.id}>
                  <LabeledControl
                    control={
                      <Switch
                        checked={selectedGraphs[graph.id] || false}
                        onChange={() => handleToggleGraph(graph.id)}
                      />
                    }
                    label={<Label>{graph.label}</Label>}
                  />
                  {graph.subOptions && selectedGraphs[graph.id] && (
                    <Box
                      css={{
                        marginLeft: '800',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '300',
                        marginTop: '300',
                      }}
                    >
                      {graph.subOptions.map((subOption) => (
                        <LabeledControl
                          key={subOption.id}
                          control={
                            <Switch
                              checked={selectedGraphs[subOption.id] || false}
                              onChange={() => handleToggleSubOption(graph.id, subOption.id)}
                            />
                          }
                          label={<Label>{subOption.label}</Label>}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'flex-end', gap: '400' }}>
          <Button tone="neutral" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={!hasSelection || isExporting}
            loading={isExporting}
            emphasis="filled"
          >
            Export
          </Button>
        </Box>
      }
    />
  );
};
